import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { SelectOption } from '../../../../ui/gzDropdown/types';
import { GridEvents } from '../../../types/common';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { GridFooterPaginationTranslations } from '../../../gridFooter/types';
import { DOMEventDispatcher } from '../../../../utils/event.utils';
import { GzInputNumberWebComponent } from '../../../../ui/gzInput';
import { FontSize } from '../../../../ui/gzIconButton/types';
import { ButtonColors } from '../../../../ui/gzButton/types';

export class GzPaginationWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Boolean, reflect: true })
  isDisabled = true;

  @property({ type: Object, reflect: true })
  translations: GridFooterPaginationTranslations = {
    pageSizeText: '',
    pageCountText: '',
  };

  @property({ type: Array, attribute: true, reflect: true })
  pageSizeOptions: Array<SelectOption> = [];

  @property({ type: Number, attribute: true, reflect: true })
  pageSize = 0;

  @property({ type: Number, attribute: true, reflect: true })
  totalNumberOfPages = 0;

  @property({ type: Number, attribute: true, reflect: true })
  currentPage = 0;

  @query('.first-page')
  private firstPageElement: HTMLElement;

  @query('.prev-page')
  private prevPageElement: HTMLElement;

  @query('.next-page')
  private nextPageElement: HTMLElement;

  @query('.last-page')
  private lastPageElement: HTMLElement;

  @query('#pageNumberInput')
  private pageNumberInput: GzInputNumberWebComponent;

  @query('#pageSize')
  private pageSizeElement: GzDropdownWebComponent;

  @state()
  private pageSizeSelectedOption: SelectOption | null = null;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('totalNumberOfPages')) {
      this.handleTotalNumberOfPagesChange();
    }

    if (changedProperties.has('pageSize') || changedProperties.has('pageSizeOptions')) {
      this.selectPageSize(this.pageSize);
    }
  }

  private handleTotalNumberOfPagesChange() {
    if (this.currentPage > this.totalNumberOfPages) {
      this.currentPage = this.totalNumberOfPages;
      DOMEventDispatcher.dispatchGlobalEvent(this, GridEvents.SET_PAGE_NUMBER_WITHOUT_RELOADING, { pageNumber: this.currentPage });
    }
    this.pageNumberInput.requestUpdate('value');
  }

  private hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  private hasNextPage(): boolean {
    return this.currentPage < this.totalNumberOfPages;
  }

  private previousButtonsDisabled(): boolean {
    return !this.hasPreviousPage();
  }

  private nextButtonsDisabled(): boolean {
    return !this.hasNextPage();
  }

  render() {
    return html`
      <div class="grid-pagination">
        <div class="items-per-page">
          <span>${this.translations.pageSizeText}</span>
          <gz-dropdown
            id="pageSize"
            @selectionChanged="${this.handlePageSizeChange}"
            listPosition="top"
            itemsVisible="4"
            value="${this.pageSize}"
            .options="${this.pageSizeOptions}"
            .selectedOption="${this.pageSizeSelectedOption}"
          ></gz-dropdown>
        </div>
        <div class="navigation">
          ${this.renderGzIconButton(
            () => this.handleFirstPageChange(),
            'ds-icon-pagination_first_page',
            'nav-item first-page',
            this.previousButtonsDisabled() || this.isDisabled
          )}
          ${this.renderGzIconButton(
            () => this.handlePrevPageChange(),
            'ds-icon-arrow_left_collapse',
            'nav-item prev-page',
            this.previousButtonsDisabled() || this.isDisabled
          )}
          ${this.renderGzIconButton(
            () => this.handleNextPageChange(),
            'ds-icon-arrow_right_expand',
            'nav-item next-page',
            this.nextButtonsDisabled() || this.isDisabled
          )}
          ${this.renderGzIconButton(
            () => this.handleLastPageChange(),
            'ds-icon-pagination_last_page',
            'nav-item last-page',
            this.nextButtonsDisabled() || this.isDisabled
          )}
        </div>
        <div class="navigating-by-page">
          <gz-input-number
            id="pageNumberInput"
            .min="${this.totalNumberOfPages > 0 ? 1 : 0}"
            .max="${this.totalNumberOfPages}"
            .value="${this.currentPage}"
            ?disabled="${this.isDisabled}"
            @change="${this.handlePageNumberChange}"
          ></gz-input-number>
          <span>${this.translations.pageCountText}</span>
        </div>
      </div>
    `;
  }

  private renderGzIconButton(
    callback?: () => void,
    icon?: string,
    className?: string,
    disabled?: boolean,
    color: ButtonColors = ButtonColors.SECONDARY,
    fontSize: FontSize = FontSize.ICON_SIZE_24
  ): TemplateResult {
    return html` <gz-icon-button
      @click=${callback}
      icon="${icon}"
      buttonColor="${color}"
      fontSize=${fontSize}
      class="${className}"
      ?disabled="${disabled}"
    ></gz-icon-button>`;
  }

  private selectPageSize(pageSize: number) {
    this.pageSizeElement.value = pageSize;
    this.pageSizeSelectedOption = this.pageSizeOptions.find(option => parseInt(option.value as string, 10) === pageSize) ?? null;
  }

  private handlePageNumberChange(event: CustomEvent) {
    if (event.detail.sourceEvent === this.currentPage) {
      return;
    }
    this.currentPage = event.detail.sourceEvent;
    DOMEventDispatcher.dispatchGlobalEvent(this, GridEvents.SET_PAGE_NUMBER, { pageNumber: this.currentPage });
  }

  private handlePageSizeChange(event: CustomEvent) {
    this.pageSize = parseInt(event.detail.value, 10);

    const pageSize = new CustomEvent(GridEvents.SET_PAGE_SIZE, {
      detail: {
        pageSize: this.pageSize,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(pageSize);
  }

  private handleNextPageChange() {
    const nextPageEvent = new CustomEvent(GridEvents.LOAD_NEXT_PAGE, {
      detail: {},
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(nextPageEvent);

    if (this.currentPage !== this.totalNumberOfPages) {
      this.currentPage += 1;
    }
  }

  private handlePrevPageChange() {
    const prevPageEvent = new CustomEvent(GridEvents.LOAD_PREVIOUS_PAGE, {
      detail: {},
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(prevPageEvent);

    if (this.currentPage !== 1) {
      this.currentPage -= 1;
    }
  }

  private handleFirstPageChange() {
    const firstPageEvent = new CustomEvent(GridEvents.LOAD_FIRST_PAGE, {
      detail: {},
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(firstPageEvent);

    this.currentPage = 1;
  }

  private handleLastPageChange() {
    const lastPageEvent = new CustomEvent(GridEvents.LOAD_LAST_PAGE, {
      detail: {},
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(lastPageEvent);

    this.currentPage = this.totalNumberOfPages;
  }
}
